import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { mayRenderMember, MemberState } from "lib/member";
import {
  endIfSuperseded,
  extendSession,
  hasExpired,
  LinkOutcome,
  MemberSession,
  openLink,
  parseIssuedAt,
  readLatestScan,
  readSession,
  recordScan,
  writeSession,
} from "lib/session";
import HoverTextButton from "render/components/HoverTextButton";
import { useMember } from "render/hooks/useMember";
import { useOpenDermaAssessment } from "render/hooks/useOpenDermaAssessment";
import LineThrobberBusyView from "render/ui/views/LineThrobberBusyView";

// How often to compare the clock with the expiry while the page is open, so a member is
// gone within a second of the session ending. A single long timer is not enough on its
// own: iOS suspends timers while the phone sleeps.
const CHECK_INTERVAL_MS = 1000;

/** The member once resolved, which is when SessionTimeout renders a member page */
export type ResolvedMember = Exclude<MemberState, { status: "loading" }>;

const ActivityContext = createContext<(() => boolean) | null>(null);
const MemberContext = createContext<ResolvedMember | null>(null);

// Asks whether the page may be uncovered. The session sets it while it runs.
type MayReveal = { current: () => boolean };
const RevealContext = createContext<MayReveal | null>(null);

const useStyles = makeStyles({
  AssessmentCheckFailed: {
    height: "66vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    textAlign: "center",
  },
  PrivacyCover: {
    position: "fixed",
    inset: 0,
    // Above everything a member page shows, including the fullscreen image
    zIndex: 100000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#282c34",
    color: "#fff8",
    fontSize: "14px",
    "&[hidden]": {
      display: "none",
    },
  },
});

/**
 * Guards every route that shows a member (CAPA #44365, AB#121888). A link opens the member
 * only when all of these hold, in this order:
 *
 * 1. The link was minted recently enough, see openLink.
 * 2. A tab coming back to its own session has not run out or been overtaken. One that has
 *    closes before anything of the member is fetched, see MemberLink.
 * 3. The member's data is held in the region this app serves. If it is not, the page shows
 *    where it is held instead, see memberReplacementPage.
 * 4. The member has a derma assessment open for images, see useOpenDermaAssessment.
 *
 * Opening the link then makes it the one open link on the phone, which closes every other
 * tab, see endIfSuperseded. Its pages close after ten minutes without the operator adding
 * an image, and send the tab back to the start page. Whatever state the member's page is
 * in, it is covered while it is in the background, see MemberCheck.
 */
export default function SessionTimeout() {
  const { patientId, externalId } = useParams();
  const memberId = patientId ?? externalId ?? "";

  const [searchParams] = useSearchParams();
  const issuedAt = parseIssuedAt(searchParams.get("issuedAt"));

  return (
    <MemberLink
      key={`${memberId}:${issuedAt}`}
      memberId={memberId}
      issuedAt={issuedAt}
    />
  );
}

interface MemberLinkProps {
  memberId: string;
  issuedAt: number;
}

type MemberLinkOutcome =
  | LinkOutcome
  | { kind: "ended"; session: MemberSession };

function MemberLink({ memberId, issuedAt }: MemberLinkProps) {
  const [outcome] = useState((): MemberLinkOutcome => {
    const now = Date.now();
    const opened = openLink(
      readSession(window.sessionStorage, memberId),
      issuedAt,
      now
    );
    if (opened.kind !== "continue") {
      return opened;
    }

    // A tab that already ran out, or that a newer scan overtook, closes before anything
    // of the member is fetched, so it says the session expired even while offline
    const session = endIfSuperseded(
      opened.session,
      readLatestScan(window.localStorage),
      now
    );
    return hasExpired(session, now) ? { kind: "ended", session } : opened;
  });

  useEffect(() => {
    if (outcome.kind === "ended") {
      writeSession(window.sessionStorage, memberId, outcome.session);
    }
  }, [memberId, outcome]);

  if (outcome.kind === "expired") {
    return <LeaveMember reason="linkExpired" />;
  }

  if (outcome.kind === "ended") {
    return <LeaveMember reason="sessionTimeout" />;
  }

  return (
    <MemberCheck
      memberId={memberId}
      initialSession={outcome.session}
      isStart={outcome.kind === "start"}
    />
  );
}

interface MemberCheckProps {
  memberId: string;
  initialSession: MemberSession;
  isStart: boolean;
}

function MemberCheck({ memberId, initialSession, isStart }: MemberCheckProps) {
  const classes = useStyles();
  const member = useMember(memberId);
  const { check, retry } = useOpenDermaAssessment(
    memberId,
    mayRenderMember(member)
  );

  // Safari keeps a screenshot of every tab for its tab overview, and iOS one of Safari for
  // the app switcher, taken as the page leaves the screen. A suspended tab cannot redraw
  // itself later, so every state of a member's page is covered the moment it goes to the
  // background, the member's name on the region notice included. The cover is set on the
  // element directly, so it is up before any render could run, and it sits on top of the
  // page rather than replacing it, so a file input survives should the camera hide the page.
  // A blur does not cover the page: iOS sends no focus back for every blur, and reports no
  // focus even with the page on screen, so a cover set on blur stayed up until a tap.
  const privacyCover = useRef<HTMLDivElement>(null);
  const mayReveal = useRef(() => true);

  // A page loaded in the background starts covered. Read once, so that a render never
  // undoes a cover set on the element.
  const [startsCovered] = useState(() => document.visibilityState === "hidden");

  const coverPage = useCallback(() => {
    if (privacyCover.current) {
      privacyCover.current.hidden = false;
    }
  }, []);

  // Uncovers the page once it is back on screen, unless the session ended meanwhile. The
  // page has to be visible: pageshow also fires when a page first loads, which may be in the
  // background.
  const revealPage = useCallback(() => {
    if (
      document.visibilityState === "visible" &&
      mayReveal.current() &&
      privacyCover.current
    ) {
      privacyCover.current.hidden = true;
    }
  }, []);

  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === "hidden") {
        coverPage();
      } else {
        revealPage();
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", coverPage);
    window.addEventListener("pageshow", revealPage);
    window.addEventListener("focus", revealPage);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", coverPage);
      window.removeEventListener("pageshow", revealPage);
      window.removeEventListener("focus", revealPage);
    };
  }, [coverPage, revealPage]);

  function renderAssessmentCheck() {
    switch (check.status) {
      case "checking":
        return <LineThrobberBusyView />;
      case "failed":
        return (
          <div className={classes.AssessmentCheckFailed}>
            <h1>Could not check this member's derma assessment</h1>
            {check.message}
            <HoverTextButton onClick={retry}>Try again</HoverTextButton>
          </div>
        );
      case "none":
        return <LeaveMember reason="noAssessment" />;
      case "open":
        return (
          <MemberSessionTimeout
            memberId={memberId}
            initialSession={initialSession}
            isStart={isStart}
          />
        );
    }
  }

  function renderMember() {
    if (member.status === "loading") {
      return <LineThrobberBusyView />;
    }

    return (
      <MemberContext.Provider value={member}>
        {member.status === "withheld" ? (
          // The page shows only where the member's data is held, nothing of their record,
          // so there is no assessment to check here and no session to start
          <Outlet />
        ) : (
          renderAssessmentCheck()
        )}
      </MemberContext.Provider>
    );
  }

  return (
    <RevealContext.Provider value={mayReveal}>
      {renderMember()}
      {/* A tap uncovers the page, should it stay covered with the page in view */}
      <div
        ref={privacyCover}
        className={classes.PrivacyCover}
        hidden={!startsCovered}
        onClick={revealPage}
      >
        Tap to continue
      </div>
    </RevealContext.Provider>
  );
}

/** Leaves the member's URL for the start page, which says why */
function LeaveMember({
  reason,
}: {
  reason: "sessionTimeout" | "linkExpired" | "noAssessment";
}) {
  const navigate = useNavigate();

  useEffect(() => {
    // Replace, so that Back cannot return to the member
    navigate(`/?${reason}=true`, { replace: true });
  }, [navigate, reason]);

  return null;
}

interface MemberSessionTimeoutProps {
  memberId: string;
  initialSession: MemberSession;
  isStart: boolean;
}

function MemberSessionTimeout({
  memberId,
  initialSession,
  isStart,
}: MemberSessionTimeoutProps) {
  const session = useRef<MemberSession>();
  const mayReveal = useContext(RevealContext);
  if (!session.current) {
    const now = Date.now();
    // A link's ten minutes start once the member opens, so a slow or retried check does not
    // use them up. Which tab wins still goes by when each link was opened.
    const opened = isStart
      ? extendSession(initialSession, now)
      : initialSession;
    session.current = endIfSuperseded(
      opened,
      readLatestScan(window.localStorage),
      now
    );
  }

  const [expired, setExpired] = useState(() =>
    hasExpired(session.current!, Date.now())
  );

  useEffect(() => {
    writeSession(window.sessionStorage, memberId, session.current!);
    // Only a link that starts a session takes over the phone, so a reload or Back cannot
    // close the other tabs, and neither can a scan that a newer one already overtook
    if (isStart && !hasExpired(session.current!, Date.now())) {
      recordScan(window.localStorage, session.current!.startedAt, Date.now());
    }
  }, [memberId, isStart]);

  // Ends the session if it ran out or another link was opened since, and says whether it did
  const endIfOver = useCallback(
    (now: number) => {
      session.current = endIfSuperseded(
        session.current!,
        readLatestScan(window.localStorage),
        now
      );
      if (!hasExpired(session.current, now)) {
        return false;
      }
      writeSession(window.sessionStorage, memberId, session.current);
      setExpired(true);
      return true;
    },
    [memberId]
  );

  // The cover stays up over a member whose session ended while the page was away
  useEffect(() => {
    if (!mayReveal) {
      return;
    }
    mayReveal.current = () => !endIfOver(Date.now());
    return () => {
      mayReveal.current = () => true;
    };
  }, [mayReveal, endIfOver]);

  useEffect(() => {
    if (expired) {
      return;
    }

    function check() {
      endIfOver(Date.now());
    }

    const interval = setInterval(check, CHECK_INTERVAL_MS);
    // Check the moment the phone wakes or the tab comes back, before the operator can look.
    // A focus on the page in view is checked by the cover, through mayReveal.
    document.addEventListener("visibilitychange", check);
    window.addEventListener("pageshow", check);
    // Another tab opened a link
    window.addEventListener("storage", check);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("pageshow", check);
      window.removeEventListener("storage", check);
    };
  }, [expired, endIfOver]);

  const onActivity = useCallback(() => {
    const now = Date.now();
    if (endIfOver(now)) {
      return false;
    }
    session.current = extendSession(session.current!, now);
    writeSession(window.sessionStorage, memberId, session.current);
    return true;
  }, [memberId, endIfOver]);

  // Render nothing of the member past the session, even before the redirect lands
  if (expired) {
    return <LeaveMember reason="sessionTimeout" />;
  }

  return (
    <ActivityContext.Provider value={onActivity}>
      <Outlet />
    </ActivityContext.Provider>
  );
}

function ignoreActivity() {
  return true;
}

/**
 * Call on operator activity that should keep the member's session open. Answers whether
 * the session is still open: when it is not, the member's page is about to close and the
 * activity must not go ahead.
 */
export function useSessionActivity() {
  return useContext(ActivityContext) ?? ignoreActivity;
}

/**
 * The member on the current route, resolved once for all of its pages. Every member page
 * renders inside SessionTimeout, which provides it once the member has loaded.
 */
export function useSessionMember(): ResolvedMember {
  const member = useContext(MemberContext);
  if (!member) {
    throw new Error("useSessionMember outside SessionTimeout");
  }
  return member;
}
